import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { toast } from '@/hooks/use-toast';
import { readRecentClientErrors, clearClientErrors } from '@/utils/clientErrorBuffer';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { redactPII, scrubDeep } from '@/utils/scrubBugContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, X } from 'lucide-react';
import { toJson } from '@/utils/toJson';

interface BugReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BugReportModal({ open, onOpenChange }: BugReportModalProps) {
  const { user, activeShop, membership, isPassportMode, hasScopeAccess } = useAuth();
  // FIX-R35-HIGH-4: pull support-session so bug reports filed during a
  // support handoff carry the session id for audit correlation.
  const { supportSession, isInSupportMode } = useSupportMode();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  
  // Form state
  const [summary, setSummary] = useState('');
  const [tryingToDo, setTryingToDo] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [frequency, setFrequency] = useState('');
  const [severity, setSeverity] = useState('');
  const [consentFollowUp, setConsentFollowUp] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      
      // Validate file types and sizes
      const validFiles = newFiles.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isUnder10MB = file.size <= 10 * 1024 * 1024;
        
        if (!isImage) {
          toast({
            title: "Invalid file type",
            description: `${file.name} is not an image file`,
            variant: "destructive",
          });
          return false;
        }
        
        if (!isUnder10MB) {
          toast({
            title: "File too large",
            description: `${file.name} exceeds 10MB limit`,
            variant: "destructive",
          });
          return false;
        }
        
        return true;
      });
      
      setFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const captureContext = () => {
    const urlParams = new URLSearchParams(location.search);
    const ticketId = urlParams.get('ticket');
    const boardDate = urlParams.get('date');

    // FIX-R35-HIGH-4: include role/tier/support-session + the last 20
    // auto-captured client errors from the sessionStorage ring buffer.
    // These were populated by ErrorBoundary + window error relays so
    // support gets a full trace even when the user can't articulate the
    // reproduction steps.
    // FIX-R132-MED-8: also include the in-memory breadcrumb trail so
    // support can see the OB:GUARD → OB:COMMIT → OB:FAIL → OB:BUMP
    // sequence that led up to the user's report. Breadcrumbs are bounded
    // (last 100) and live in-memory only, so they would otherwise vanish
    // on report submission. We snapshot the last 50 to keep the payload
    // bounded.
    const recentClientErrors = readRecentClientErrors();
    const recentBreadcrumbs = breadcrumbs.get(50);

    return {
      // SUP-2 (K37): redact PII from the route (query params may carry an
      // email/phone/VIN) and deep-scrub the error/breadcrumb tails before
      // they are persisted to bug_reports.context_json.
      route: redactPII(location.pathname + location.search),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      // SUP-5 (K37): honest environment label. Prefer the build-time
      // VITE_APP_ENV (set per deploy: development/staging/production); fall
      // back to dev-vs-prod. Previously dev was mislabeled "staging".
      environment:
        (import.meta.env.VITE_APP_ENV as string | undefined) ||
        (import.meta.env.DEV ? 'development' : 'production'),
      ticketId: ticketId || null,
      boardDate: boardDate || null,
      timestamp: new Date().toISOString(),
      consentFollowUp,
      // identity + access surface
      membershipRole: membership?.role || null,
      membershipStatus: membership?.status || null,
      shopTier: activeShop?.tier || null,
      isPassportMode,
      hasScopeAccess,
      // support mode audit linkage
      isInSupportMode,
      supportSessionId: supportSession?.sessionId || null,
      supportTargetShopId: supportSession?.targetShopId || null,
      supportMode: supportSession?.mode || null,
      // SUP-2: recent client errors (capped by buffer), PII-redacted.
      recentClientErrors: scrubDeep(recentClientErrors),
      // FIX-R132-MED-8: in-memory breadcrumb tail (placement guards,
      // commits, fails, bumps) for action-sequence reconstruction —
      // SUP-2: deep-scrubbed for PII.
      recentBreadcrumbs: scrubDeep(recentBreadcrumbs),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !activeShop) {
      toast({
        title: "Authentication required",
        description: "Please log in to submit a bug report",
        variant: "destructive",
      });
      return;
    }

    // Validate required fields
    if (!summary.trim() || !tryingToDo.trim() || !whatHappened.trim() || !frequency || !severity) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    // SUP-4 (K37): the full context object can contain PII fragments — only
    // log it in dev, never to production console / log shippers.
    if (import.meta.env.DEV) console.log('🐛 Starting bug report submission...');

    try {
      const context = captureContext();
      if (import.meta.env.DEV) console.log('📊 Context captured:', context);

      // Insert bug report
      if (import.meta.env.DEV) console.log('💾 Inserting bug report into database...');
      const { data: bugReport, error: insertError } = await supabase
        .from('bug_reports')
        .insert({
          user_id: user.id,
          tenant_id: activeShop.id,
          plan_tier: activeShop.tier || 'CORE',
          environment: context.environment,
          // CORE-BJ (GAP-006): the reporter's free text gets the same PII/secret redaction as the
          // captured context. People paste a customer's phone, a VIN or a token while describing a
          // problem; support needs the problem, not the customer's data.
          summary: redactPII(summary),
          trying_to_do: redactPII(tryingToDo),
          what_happened: redactPII(whatHappened),
          frequency,
          severity,
          has_attachments: files.length > 0,
          context_json: toJson(context),
          browser_info: navigator.userAgent,
          route_path: redactPII(location.pathname),
          shop_name: activeShop.name,
          user_email: user.email || '',
        })
        .select()
        .single();

      if (insertError) {
        console.error('❌ Database insertion error:', insertError);
        throw new Error(`Failed to save bug report: ${insertError.message}`);
      }
      
      console.log('✅ Bug report saved successfully:', bugReport.id);

      // Upload attachments if any (non-blocking - report saved even if uploads fail)
      let uploadsFailed = false;
      if (files.length > 0 && bugReport) {
        console.log(`📎 Uploading ${files.length} attachments...`);
        
        try {
          const uploadPromises = files.map(async (file, index) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${bugReport.id}_${index}.${fileExt}`;
            const filePath = `${activeShop.id}/${bugReport.id}/${fileName}`;

            // SUP-4: filenames are user-controlled (may embed PII) — DEV-only.
            if (import.meta.env.DEV) console.log(`📤 Uploading file ${index + 1}/${files.length}: ${fileName}`);
            const { error: uploadError } = await supabase.storage
              .from('bug_attachments')
              .upload(filePath, file);

            if (uploadError) {
              if (import.meta.env.DEV) console.error(`❌ Upload error for ${fileName}:`, uploadError);
              throw uploadError;
            }

            if (import.meta.env.DEV) console.log(`✅ Uploaded ${fileName}`);
          });

          await Promise.all(uploadPromises);
          console.log('✅ All attachments uploaded successfully');
        } catch (uploadError: any) {
          console.error('⚠️ Attachment upload failed:', uploadError);
          uploadsFailed = true;
          // Don't throw - allow report to be saved even if attachments fail
        }
      }

      // Call edge function to send email notification (non-blocking)
      try {
        console.log('📧 Triggering email notification...');
        await supabase.functions.invoke('send-bug-report-email', {
          body: { bug_report_id: bugReport.id }
        });
        console.log('✅ Email notification triggered');
      } catch (emailError) {
        console.error('⚠️ Email notification failed (non-critical):', emailError);
        // Don't fail the submission if email fails
      }

      console.log('✅ Bug report submission complete');
      
      // FIX-R35-HIGH-4: now that the recent-errors buffer has been persisted
      // on the bug report, clear it so a follow-up report doesn't re-attach
      // the same stack traces.
      clearClientErrors();

      toast({
        title: "Bug report submitted",
        description: uploadsFailed
          ? "Report saved, but some attachments failed to upload. We'll still review your report."
          : "Thank you for helping us improve the app!",
      });

      // Reset form
      setSummary('');
      setTryingToDo('');
      setWhatHappened('');
      setFrequency('');
      setSeverity('');
      setFiles([]);
      setConsentFollowUp(false);
      onOpenChange(false);
    } catch (error: any) {
      console.error('❌ Bug report submission failed:', error);
      toast({
        title: "Submission failed",
        description: error.message || "Please try again. If the problem persists, contact support.",
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>
            Help us improve OptiBay by reporting bugs or issues you encounter.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="summary">Short summary of the issue *</Label>
            <Input
              id="summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g., Dragging tickets causes gaps in the schedule"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tryingToDo">What were you trying to do? *</Label>
            <Textarea
              id="tryingToDo"
              value={tryingToDo}
              onChange={(e) => setTryingToDo(e.target.value)}
              placeholder="Describe what you were attempting to accomplish"
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatHappened">What happened instead? *</Label>
            <Textarea
              id="whatHappened"
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
              placeholder="Describe the unexpected behavior or error"
              rows={3}
              required
            />
            <p className="text-xs text-muted-foreground" data-testid="bug-report-redaction-notice">
              Phone numbers, email addresses and VINs are removed automatically before the report is saved. Never paste a password.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frequency">How often does this happen? *</Label>
              <Select value={frequency} onValueChange={setFrequency} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Always">Always</SelectItem>
                  <SelectItem value="Sometimes">Sometimes</SelectItem>
                  <SelectItem value="Rarely">Rarely</SelectItem>
                  <SelectItem value="Not sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Severity *</Label>
              <Select value={severity} onValueChange={setSeverity} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Blocks me from using the app">
                    Blocks me from using the app
                  </SelectItem>
                  <SelectItem value="Major problem but I can work around it">
                    Major problem but I can work around it
                  </SelectItem>
                  <SelectItem value="Minor issue / annoyance">
                    Minor issue / annoyance
                  </SelectItem>
                  <SelectItem value="Just feedback / suggestion">
                    Just feedback / suggestion
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Screenshots (optional)</Label>
            <div className="border-2 border-dashed rounded-lg p-4">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload screenshots (max 5, 5MB each)
                </span>
              </label>
              
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-muted p-2 rounded"
                    >
                      <span className="text-sm truncate">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="consent"
              checked={consentFollowUp}
              onCheckedChange={(checked) => setConsentFollowUp(checked as boolean)}
            />
            <Label
              htmlFor="consent"
              className="text-sm font-normal cursor-pointer"
            >
              It's okay to contact me about this issue if needed
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
